import type { CursorPaginationState } from '@app/hooks/useCursorPagination';

export interface InventoryFilters {
  q: string;
  state: string;
  vendor: string;
  siteId: string;
  role: string;
  model: string;
  mgmtAddress: string;
  labelKey: string;
  labelValue: string;
  sort: 'recent' | 'oldest';
}

export type InventoryTab = 'devices' | 'cmdb-bindings';

export const DEFAULT_INVENTORY_FILTERS: InventoryFilters = {
  q: '', state: 'all', vendor: 'all', siteId: '', role: 'all', model: '',
  mgmtAddress: '', labelKey: '', labelValue: '', sort: 'recent'
};

export const INVENTORY_PAGE_SIZES = [10, 25, 50];

const FILTER_KEYS = [
  'q', 'state', 'vendor', 'siteId', 'role', 'model', 'mgmtAddress',
  'labelKey', 'labelValue', 'sort'
];

const readPositiveInt = (value: string | null, fallback: number): number => {
  const parsed = Number(value);
  return value && Number.isSafeInteger(parsed) && parsed > 0 ? parsed : fallback;
};

export const readInventoryRouteState = (params: URLSearchParams) => {
  const filters: InventoryFilters = {
    q: params.get('q') ?? '',
    state: params.get('state') || 'all',
    vendor: params.get('vendor') || 'all',
    siteId: params.get('siteId') ?? '',
    role: params.get('role') || 'all',
    model: params.get('model') ?? '',
    mgmtAddress: params.get('mgmtAddress') ?? '',
    labelKey: params.get('labelKey') ?? '',
    labelValue: params.get('labelValue') ?? '',
    sort: params.get('sort') === 'oldest' ? 'oldest' : 'recent'
  };
  const requestedSize = readPositiveInt(params.get('pageSize'), INVENTORY_PAGE_SIZES[0]);
  const pageSize = INVENTORY_PAGE_SIZES.includes(requestedSize) ? requestedSize : INVENTORY_PAGE_SIZES[0];
  const requestedPage = readPositiveInt(params.get('page'), 1);
  const after = params.get('after') || undefined;
  const before = params.get('before') || undefined;
  const cursor = requestedPage > 1 && after
    ? { direction: 'after' as const, value: after }
    : requestedPage > 1 && before
      ? { direction: 'before' as const, value: before }
      : { direction: null };
  const page = cursor.direction ? requestedPage : 1;
  return {
    filters,
    tab: params.get('tab') === 'cmdb-bindings' ? 'cmdb-bindings' as InventoryTab : 'devices' as InventoryTab,
    page,
    pageSize,
    cursor
  };
};

export const withInventoryFilters = (params: URLSearchParams, filters: InventoryFilters): URLSearchParams => {
  const next = new URLSearchParams(params);
  FILTER_KEYS.forEach((key) => next.delete(key));
  (Object.keys(DEFAULT_INVENTORY_FILTERS) as Array<keyof InventoryFilters>).forEach((key) => {
    const value = filters[key];
    if (value && value !== DEFAULT_INVENTORY_FILTERS[key]) next.set(key, value);
  });
  next.delete('page');
  next.delete('after');
  next.delete('before');
  return next;
};

export const withInventoryPagination = (
  params: URLSearchParams,
  state: CursorPaginationState
): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.delete('page');
  next.delete('pageSize');
  next.delete('after');
  next.delete('before');
  if (state.pageSize !== INVENTORY_PAGE_SIZES[0]) next.set('pageSize', String(state.pageSize));
  if (state.page > 1 && state.cursor.value) {
    next.set('page', String(state.page));
    if (state.cursor.direction === 'after') next.set('after', state.cursor.value);
    if (state.cursor.direction === 'before') next.set('before', state.cursor.value);
  }
  return next;
};

export const withInventoryTab = (params: URLSearchParams, tab: InventoryTab): URLSearchParams => {
  const next = new URLSearchParams(params);
  if (tab === 'devices') next.delete('tab');
  else next.set('tab', tab);
  return next;
};

export const inventoryReturnPath = (from: unknown): string => {
  if (typeof from !== 'string') return '/app/inventory';
  try {
    const url = new URL(from, window.location.origin);
    return url.origin === window.location.origin && url.pathname === '/app/inventory'
      ? `${url.pathname}${url.search}${url.hash}`
      : '/app/inventory';
  } catch {
    return '/app/inventory';
  }
};
